import { useState } from 'react';
import { 
  ArrowLeft, 
  Users, 
  Swords, 
  Scroll, 
  Shield, 
  Skull, 
  Trophy,
  Target,
  Eye,
  UsersRound,
  CheckCircle,
  XCircle,
  Clock,
  Moon,
  Sparkles,
  Crown,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { useTheme } from '../lib/theme-provider';
import { ROLES, FactionType } from '../types';

type TabType = 'rules' | 'roles' | 'setup';

// 游戏目标数据
const VICTORY_CONDITIONS = {
  good: {
    title: '正义阵营',
    subtitle: '亚瑟王的追随者',
    color: 'blue',
    icon: Shield,
    conditions: [
      { icon: CheckCircle, text: '成功完成 3 个任务' },
      { icon: Shield, text: '保护梅林不被刺客刺杀' },
    ],
  },
  evil: {
    title: '邪恶阵营',
    subtitle: '莫德雷德的追随者',
    color: 'red',
    icon: Skull,
    conditions: [
      { icon: XCircle, text: '破坏 3 个任务' },
      { icon: Target, text: '游戏结束时刺杀梅林' },
    ],
  },
};

// 游戏流程数据
const GAME_FLOW = [
  {
    id: 1,
    title: '准备阶段',
    description: '分发身份牌，确认角色',
    icon: UsersRound,
    color: 'from-blue-500/20 to-purple-500/20',
    borderColor: 'border-blue-500/50',
  },
  {
    id: 2,
    title: '夜间环节',
    description: '特殊角色睁眼确认身份',
    icon: Moon,
    color: 'from-purple-500/20 to-indigo-500/20',
    borderColor: 'border-purple-500/50',
  },
  {
    id: 3,
    title: '任务阶段',
    description: '组队并投票决定是否执行任务',
    icon: Swords,
    color: 'from-amber-500/20 to-orange-500/20',
    borderColor: 'border-amber-500/50',
  },
  {
    id: 4,
    title: '投票阶段',
    description: '队员秘密投票任务成败',
    icon: CheckCircle,
    color: 'from-emerald-500/20 to-teal-500/20',
    borderColor: 'border-emerald-500/50',
  },
  {
    id: 5,
    title: '游戏结束',
    description: '达成胜利条件或刺杀梅林',
    icon: Trophy,
    color: 'from-avalon-gold/20 to-yellow-500/20',
    borderColor: 'border-avalon-gold/50',
  },
];

// 任务规则数据
const MISSION_RULES = [
  { icon: Users, title: '5轮任务', desc: '每轮需要不同数量的队员参与' },
  { icon: Eye, title: '秘密投票', desc: '队员秘密选择成功或失败卡片' },
  { icon: XCircle, title: '失败条件', desc: '只要有1张失败卡，任务就失败（第4轮需要2张）' },
  { icon: Trophy, title: '胜利条件', desc: '先完成3个任务的一方获胜' },
];

// 刺杀阶段数据
const ASSASSINATION_RULES = [
  { icon: Clock, title: '触发条件', desc: '正义阵营完成3个任务后进入刺杀阶段' },
  { icon: Target, title: '刺杀行动', desc: '刺客可以猜测谁是梅林' },
  { icon: Trophy, title: '胜负判定', desc: '猜对则邪恶阵营获胜，猜错则正义阵营获胜' },
];

// V98: 扩展规则数据
const EXTENDED_RULES = {
  ladyOfLake: {
    name: '湖中仙女',
    englishName: 'Lady of the Lake',
    icon: '🔮',
    minPlayers: 10,
    items: [
      { title: '道具说明', desc: '湖中仙女卡牌代表"信任传递"的象征，持有者拥有查验他人阵营的权力。' },
      { title: '起始持有', desc: '游戏开始时，湖中仙女由初始队长的上家担任。' },
      { title: '使用规则', desc: '在第2轮任务阶段结束后（无论任务成功或失败），当前持有者可以指定任意一名其他玩家查验身份，对方必须如实告知真实阵营归属（无需透露具体角色），查验结果仅对查验者本人可见。被查验的玩家成为下一轮湖中仙女，并在下一轮任务阶段结束后继续选择未担任过湖中仙女的任意其他角色进行查验，以此类推。' },
      { title: '策略提示', desc: '好人阵营可通过湖中仙女快速建立信任链，但需警惕坏人伪装传递。坏人持有时可故意传递给队友制造"金水"假象，或传递给好人混淆视听。' },
    ],
  },
  excalibur: {
    name: '王者之剑',
    englishName: 'Excalibur',
    icon: '⚔️',
    minPlayers: 10,
    items: [
      { title: '道具说明', desc: '王者之剑是好人阵营的秘密武器，可用于反转任务投票结果。' },
      { title: '持有条件', desc: '王者之剑由当前任务的队长持有。' },
      { title: '使用规则', desc: '持有王者之剑的玩家可以在当前任务结果公布前（无论成功或失败），立即宣布使用王者之剑，选择任务中除自己以外的其他一名玩家，将其已投的任务票暗置变更，强制其个人的任务票反转（成功变失败，失败变成功）。' },
      { title: '策略提示', desc: '好人阵营可在关键任务强制邪恶阵营投出成功票；坏人阵营可利用机制达成任务破坏目标。' },
    ],
  },
};

const SETUP_INFO = [
  {
    players: 5,
    good: 3,
    evil: 2,
    missions: [2, 3, 2, 3, 3],
    fails: [1, 1, 1, 1, 1],
    goodRoles: ['梅林', '派西维尔', '忠臣'],
    evilRoles: ['莫甘娜', '刺客'],
  },
  {
    players: 6,
    good: 4,
    evil: 2,
    missions: [2, 3, 4, 3, 4],
    fails: [1, 1, 1, 1, 1],
    goodRoles: ['梅林', '派西维尔', '2个忠臣'],
    evilRoles: ['莫甘娜', '刺客'],
  },
  {
    players: 7,
    good: 4,
    evil: 3,
    missions: [2, 3, 3, 4, 4],
    fails: [1, 1, 1, 2, 1],
    goodRoles: ['梅林', '派西维尔', '2个忠臣'],
    // V47: 7人局邪恶阵营更正为莫甘娜、刺客、奥伯伦
    evilRoles: ['莫甘娜', '刺客', '奥伯伦'],
  },
  {
    players: 8,
    good: 5,
    evil: 3,
    missions: [3, 4, 4, 5, 5],
    fails: [1, 1, 1, 2, 1],
    goodRoles: ['梅林', '派西维尔', '3个忠臣'],
    // V47: 8人局邪恶阵营更正为莫甘娜、刺客、爪牙
    evilRoles: ['莫甘娜', '刺客', '爪牙'],
  },
  {
    players: 9,
    good: 6,
    evil: 3,
    missions: [3, 4, 4, 5, 5],
    fails: [1, 1, 1, 2, 1],
    goodRoles: ['梅林', '派西维尔', '4个忠臣'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客'],
  },
  {
    players: 10,
    good: 6,
    evil: 4,
    missions: [3, 4, 4, 5, 5],
    fails: [1, 1, 1, 2, 1],
    // V47: 湖中仙女独立标注，不放在正义阵营
    goodRoles: ['梅林', '派西维尔', '4个忠臣'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客', '奥伯伦'],
    // V47: 添加湖中仙女独立标注
    hasLady: true,
  },
];

// V30: 新增 11-13 人局配置
const EXTENDED_SETUP_INFO = [
  {
    players: 11,
    title: '11 人局配置',
    subtitle: '',
    good: 7,
    evil: 4,
    missions: [3, 4, 5, 6, 6],
    fails: [1, 1, 1, 2, 1],
    // V47: 湖中仙女独立标注
    goodRoles: ['梅林', '派西维尔', '5个忠臣'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客', '爪牙'],
    hasLady: true,
  },
  {
    players: 12,
    title: '12 人局配置',
    subtitle: '标准版',
    good: 7,
    evil: 5,
    missions: [3, 4, 5, 6, 6],
    fails: [1, 1, 1, 2, 1],
    // V47: 湖中仙女独立标注
    goodRoles: ['梅林', '派西维尔', '5个忠臣'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客', '奥伯伦×2'],
    hasLady: true,
  },
  {
    players: 12,
    title: '12 人局配置',
    subtitle: '兰斯洛特版',
    good: 7,
    evil: 5,
    missions: [3, 4, 5, 6, 6],
    fails: [1, 1, 1, 2, 1],
    // V47: 湖中仙女独立标注
    goodRoles: ['梅林', '派西维尔', '4个忠臣', '好兰斯洛特'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客', '奥伯伦', '坏兰斯洛特'],
    hasLady: true,
  },
  {
    players: 13,
    title: '13 人局配置',
    subtitle: '',
    good: 8,
    evil: 5,
    missions: [3, 4, 5, 6, 6],
    fails: [1, 1, 1, 2, 1],
    // V47: 湖中仙女独立标注
    goodRoles: ['梅林', '派西维尔', '5个忠臣'],
    evilRoles: ['莫德雷德', '莫甘娜', '刺客', '爪牙', '奥伯伦'],
    hasLady: true,
  },
];

export default function GameGuide() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('rules');
  const [selectedFaction, setSelectedFaction] = useState<FactionType | 'all'>('all');

  const filteredRoles = Object.values(ROLES).filter(
    (role) => selectedFaction === 'all' || role.faction === selectedFaction
  );

  const tabs = [
    { id: 'rules' as TabType, label: '规则', icon: Scroll },
    { id: 'roles' as TabType, label: '角色', icon: Users },
    { id: 'setup' as TabType, label: '配置', icon: Swords },
  ];

  return (
    <ThemedBackground pageName="game-guide">
    <div className="min-h-screen p-5 pt-[72px] pb-8">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-3" style={{ backgroundColor: theme.colors['bg-primary'], borderBottom: `1px solid ${theme.colors['border-secondary'] || '#5A4BFF'}35` }}>
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white">游戏指南</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
              activeTab === tab.id
                ? 'bg-avalon-gold text-avalon-bg-dark font-semibold'
                : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            <span className="text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'rules' && (
          <div className="space-y-6">
            {/* 游戏目标 - 可视化卡片 */}
            <section>
              <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-avalon-gold" />
                游戏目标
              </h2>
              <div className="grid gap-4">
                {/* 正义阵营胜利条件 */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-900/30 border border-blue-500/30 p-5">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full -mr-8 -mt-8" />
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-bold text-lg">正义阵营</h3>
                        <span className="text-xs text-blue-300">亚瑟王的追随者</span>
                      </div>
                      <div className="space-y-2 mt-3">
                        {VICTORY_CONDITIONS.good.conditions.map((condition, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-500/30 flex items-center justify-center">
                              <condition.icon className="w-3.5 h-3.5 text-blue-300" />
                            </div>
                            <span className="text-blue-100 text-sm">{condition.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 邪恶阵营胜利条件 */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-600/20 to-red-900/30 border border-red-500/30 p-5">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/10 rounded-full -mr-8 -mt-8" />
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/30">
                      <Skull className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-bold text-lg">邪恶阵营</h3>
                        <span className="text-xs text-red-300">莫德雷德的追随者</span>
                      </div>
                      <div className="space-y-2 mt-3">
                        {VICTORY_CONDITIONS.evil.conditions.map((condition, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-red-500/30 flex items-center justify-center">
                              <condition.icon className="w-3.5 h-3.5 text-red-300" />
                            </div>
                            <span className="text-red-100 text-sm">{condition.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 游戏流程 - 可视化时间轴 */}
            <section>
              <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-avalon-gold" />
                游戏流程
              </h2>
              <div className="relative">
                {/* 连接线 */}
                <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-blue-500/50 via-purple-500/50 to-amber-500/50" />
                
                <div className="space-y-4">
                  {GAME_FLOW.map((step) => (
                    <div key={step.id} className="relative flex gap-4">
                      {/* 步骤序号和图标 */}
                      <div className="relative z-10 flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} border ${step.borderColor} flex items-center justify-center shadow-lg`}>
                          <step.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="mt-2 w-6 h-6 rounded-full bg-avalon-bg-dark border-2 border-avalon-gold flex items-center justify-center text-xs font-bold text-avalon-gold">
                          {step.id}
                        </div>
                      </div>
                      
                      {/* 步骤内容 */}
                      <div className="flex-1 pt-1">
                        <div className="bg-avalon-card rounded-xl p-4 border border-avalon-purple/30">
                          <h3 className="text-white font-semibold text-base mb-1">{step.title}</h3>
                          <p className="text-avalon-text text-sm">{step.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 任务规则 - 网格卡片 */}
            <section>
              <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Swords className="w-5 h-5 text-avalon-gold" />
                任务规则
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {MISSION_RULES.map((rule, index) => (
                  <div key={index} className="bg-avalon-card rounded-xl p-4 border border-avalon-purple/30 hover:border-avalon-gold/30 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-avalon-purple/30 flex items-center justify-center mb-3">
                      <rule.icon className="w-5 h-5 text-avalon-gold" />
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-1">{rule.title}</h3>
                    <p className="text-avalon-text text-xs leading-relaxed">{rule.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 刺杀阶段 - 横向卡片 */}
            <section>
              <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-red-400" />
                刺杀阶段
              </h2>
              <div className="bg-gradient-to-r from-red-900/20 to-avalon-card rounded-xl p-5 border border-red-500/30">
                <div className="flex flex-wrap gap-3">
                  {ASSASSINATION_RULES.map((rule, index) => (
                    <div key={index} className="flex items-center gap-3 bg-avalon-bg-dark/50 rounded-lg px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                        <rule.icon className="w-4 h-4 text-red-300" />
                      </div>
                      <div>
                        <h4 className="text-white text-sm font-medium">{rule.title}</h4>
                        <p className="text-avalon-text text-xs">{rule.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* V98: 扩展规则章节 */}
            <section>
              <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Scroll className="w-5 h-5 text-avalon-gold" />
                扩展规则
              </h2>
              <p className="text-avalon-text text-sm mb-4">
                以下扩展规则可增加游戏的策略深度和变数，建议在玩家熟悉基础玩法后启用。
              </p>
              
              <div className="space-y-4">
                {/* 湖中仙女 */}
                <div className="bg-gradient-to-r from-purple-900/20 to-avalon-card rounded-xl border border-purple-500/30 overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">{EXTENDED_RULES.ladyOfLake.name}</h3>
                        <p className="text-purple-300 text-xs">{EXTENDED_RULES.ladyOfLake.englishName}</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs">
                        <Info className="w-3 h-3" />
                        <span>≥{EXTENDED_RULES.ladyOfLake.minPlayers}人</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {EXTENDED_RULES.ladyOfLake.items.map((item, idx) => (
                        <div key={idx} className="bg-avalon-bg-dark/50 rounded-lg p-3">
                          <h4 className="text-purple-300 text-sm font-medium mb-1">{item.title}</h4>
                          <p className="text-avalon-text text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 王者之剑 */}
                <div className="bg-gradient-to-r from-amber-900/20 to-avalon-card rounded-xl border border-amber-500/30 overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">{EXTENDED_RULES.excalibur.name}</h3>
                        <p className="text-amber-300 text-xs">{EXTENDED_RULES.excalibur.englishName}</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs">
                        <Info className="w-3 h-3" />
                        <span>≥{EXTENDED_RULES.excalibur.minPlayers}人</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {EXTENDED_RULES.excalibur.items.map((item, idx) => (
                        <div key={idx} className="bg-avalon-bg-dark/50 rounded-lg p-3">
                          <h4 className="text-amber-300 text-sm font-medium mb-1">{item.title}</h4>
                          <p className="text-avalon-text text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'roles' && (
          <>
            {/* Faction Filter */}
            <div className="flex gap-2 mb-4">
              {(['all', 'good', 'evil'] as const).map((faction) => (
                <button
                  key={faction}
                  onClick={() => setSelectedFaction(faction)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    selectedFaction === faction
                      ? faction === 'good'
                        ? 'bg-blue-500 text-white'
                        : faction === 'evil'
                        ? 'bg-red-500 text-white'
                        : 'bg-avalon-gold text-avalon-bg-dark'
                      : 'bg-avalon-card text-avalon-text'
                  }`}
                >
                  {faction === 'all' ? '全部' : faction === 'good' ? '正义' : '邪恶'}
                </button>
              ))}
            </div>

            {/* Role Cards - V30: 过滤掉重复的 loyalServant */}
            <div className="grid gap-4">
              {filteredRoles.filter(role => role.id !== 'loyalServant').map((role) => (
                <div
                  key={role.id}
                  className={`avalon-card border-l-4 ${
                    role.faction === 'good' ? 'border-l-blue-500' : 'border-l-red-500'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{role.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-bold text-lg">{role.name}</h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            role.faction === 'good'
                              ? 'bg-blue-500/30 text-blue-300'
                              : 'bg-red-500/30 text-red-300'
                          }`}
                        >
                          {role.factionName}
                        </span>
                      </div>
                      <p className="text-avalon-text text-sm mb-2">{role.description}</p>
                      <div className="bg-avalon-bg-dark/50 rounded-lg p-3">
                        <span className="text-avalon-gold text-xs font-medium">能力：</span>
                        <span className="text-avalon-text-light text-xs ml-1">{role.ability}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'setup' && (
          <>
            {/* 5-10 人标准配置 */}
            {SETUP_INFO.map((setup) => (
              <div key={setup.players} className="avalon-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-lg">{setup.players} 人局</h3>
                  <div className="flex gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-500/30 text-blue-300">
                      正义 {setup.good}人
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-red-500/30 text-red-300">
                      邪恶 {setup.evil}人
                    </span>
                  </div>
                </div>

                {/* Mission Requirements */}
                <div className="mb-4">
                  <div className="text-avalon-text text-xs mb-2">任务人数配置</div>
                  <div className="flex gap-2">
                    {setup.missions.map((count, index) => (
                      <div
                        key={index}
                        className="flex-1 text-center py-2 rounded-lg bg-avalon-bg-dark/50"
                      >
                        <div className="text-avalon-gold font-bold">{count}</div>
                        <div className="text-avalon-text text-xs">第{index + 1}轮</div>
                        {setup.fails[index] > 1 && (
                          <div className="text-red-400 text-xs">需{setup.fails[index]}失败</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Roles - 分阵营显示 */}
                <div className="space-y-3">
                  <div>
                    <div className="text-blue-300 text-xs mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      正义阵营
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {setup.goodRoles.map((role, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-200"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-red-300 text-xs mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      邪恶阵营
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {setup.evilRoles.map((role, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* V47: 湖中仙女独立标注 */}
                  {(setup as { hasLady?: boolean }).hasLady && (
                    <div className="mt-2 pt-2 border-t border-avalon-card">
                      <span className="text-[10px] text-purple-300/70 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400/50" />
                        有湖中仙女
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* V30: 11-13 人高级配置 */}
            <div className="mt-6 mb-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-avalon-gold/50 to-transparent" />
                <span className="text-avalon-gold text-sm font-medium">高级配置（11-13人）</span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-avalon-gold/50 to-transparent" />
              </div>
            </div>

            {EXTENDED_SETUP_INFO.map((setup, index) => (
              <div key={`extended-${index}`} className="avalon-card border-l-4 border-l-avalon-gold">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-base">
                    {setup.title}
                    {/* V47: 副标题字体调小1号 */}
                    {setup.subtitle && (
                      <span className="text-avalon-text text-xs font-normal ml-1">（{setup.subtitle}）</span>
                    )}
                  </h3>
                  <div className="flex gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-500/30 text-blue-300">
                      正义 {setup.good}人
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-red-500/30 text-red-300">
                      邪恶 {setup.evil}人
                    </span>
                  </div>
                </div>

                {/* Mission Requirements */}
                <div className="mb-4">
                  <div className="text-avalon-text text-xs mb-2">任务人数配置</div>
                  <div className="flex gap-2">
                    {setup.missions.map((count, idx) => (
                      <div
                        key={idx}
                        className="flex-1 text-center py-2 rounded-lg bg-avalon-bg-dark/50"
                      >
                        <div className="text-avalon-gold font-bold">{count}</div>
                        <div className="text-avalon-text text-xs">第{idx + 1}轮</div>
                        {setup.fails[idx] > 1 && (
                          <div className="text-red-400 text-xs">需{setup.fails[idx]}失败</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Roles - 分阵营显示 */}
                <div className="space-y-3">
                  <div>
                    <div className="text-blue-300 text-xs mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      正义阵营
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {setup.goodRoles.map((role, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-200"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-red-300 text-xs mb-2 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      邪恶阵营
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {setup.evilRoles.map((role, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* V47: 湖中仙女独立标注 */}
                  {setup.hasLady && (
                    <div className="mt-2 pt-2 border-t border-avalon-card">
                      <span className="text-[10px] text-purple-300/70 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400/50" />
                        有湖中仙女
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  </ThemedBackground>
  );
}
